import type { GuideSlug } from "@/app/(resources)/data";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import Link from "next/link";

export interface GuideHubCardData {
  slug: GuideSlug;
  progressId: string;
  title: string;
  route: string;
  text: string;
  points: string[];
  time: string;
  level: string;
}

interface GuideHubCardProps {
  card: GuideHubCardData;
  number: number;
  isDone: boolean;
  isRecommended: boolean;
  doneBadge: string;
  openLabel: string;
}

// The decorative corner circle cycles peach, mint, lilac like the preview.
const cornerTone = ["bg-tone-peach", "bg-tone-mint", "bg-tone-lilac"];

// One guide on the hub (the preview's `.gcard`): number, Done badge,
// linked title, summary, two points, time and level, and "Open the guide".
const GuideHubCard = ({
  card,
  number,
  isDone,
  isRecommended,
  doneBadge,
  openLabel,
}: GuideHubCardProps) => (
  <li
    className={cn(
      "relative grid content-start gap-2.5 overflow-hidden rounded-[30px] bg-white px-[26px] pt-7 pb-6 text-ink shadow-card transition-transform duration-300 hover:-translate-y-1.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0",
      isRecommended && "outline-3 outline-offset-3 outline-primary",
    )}
  >
    <span
      aria-hidden="true"
      className={cn(
        "absolute -top-10 -right-10 size-[150px] rounded-full",
        cornerTone[(number - 1) % cornerTone.length],
      )}
    />
    <span
      aria-hidden="true"
      className="relative text-[2.4rem] leading-none font-extrabold tracking-[-2px] text-primary"
    >
      {String(number).padStart(2, "0")}
    </span>
    {isDone && (
      <span className="absolute top-[18px] right-[18px] rounded-full bg-tone-mint px-3 py-1 text-[12.5px] font-extrabold text-tone-mint-ink">
        {doneBadge}
      </span>
    )}
    <h3 className="relative text-xl leading-snug font-bold">
      <Link href={card.route} className="hover:text-primary">
        {card.title}
      </Link>
    </h3>
    <p className="relative text-[15.5px] leading-normal text-ink-muted">{card.text}</p>
    <ul className="relative list-disc space-y-1 pl-[18px] text-[14.5px] text-ink-muted marker:text-primary">
      {card.points.map((point) => (
        <li key={point}>{point}</li>
      ))}
    </ul>
    <div className="relative mt-1.5 flex flex-wrap items-center gap-2.5">
      <span className="rounded-full bg-paper px-3 py-1 text-[12.5px] font-bold">
        {card.time}
      </span>
      <span className="rounded-full bg-paper px-3 py-1 text-[12.5px] font-bold">
        {card.level}
      </span>
      <Button asChild variant="brandOutline" size="pillSm" className="ml-auto">
        <Link href={card.route} aria-label={`${openLabel}: ${card.title}`}>
          {openLabel}
        </Link>
      </Button>
    </div>
  </li>
);

export default GuideHubCard;
